import { transformKeysToSnake, transformKeysToCamel } from '../utils/caseTransforms';
import { StructuredApiError, parseErrorResponse, JsonApiError } from './apiErrors';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// --- Interfaces ---

// Represents a resource object in JSON:API
export interface JsonApiResourceObject {
  id: string;
  type: string;
  attributes?: Record<string, any>;
  relationships?: Record<string, JsonApiRelationship>;
}

// Represents the linkage part of a relationship
export interface JsonApiResourceIdentifier {
  id: string;
  type: string;
}

// Represents a relationship object in JSON:API
export interface JsonApiRelationship {
  data: JsonApiResourceIdentifier | JsonApiResourceIdentifier[] | null;
  links?: Record<string, string>;
  meta?: Record<string, any>;
}

// Represents the top-level JSON:API document
export interface JsonApiDocument {
  data: JsonApiResourceObject | JsonApiResourceObject[] | null;
  included?: JsonApiResourceObject[];
  meta?: Record<string, any>;
  errors?: JsonApiError[] | Record<string, string[]>; // Keep error handling
  links?: Record<string, string>;
}

// Keep backward compatibility with existing interfaces
export interface JsonApiResource<T> {
  id: string;
  type: string;
  attributes: T;
}

export interface JsonApiResponse<T> {
  data: JsonApiResource<T> | JsonApiResource<T>[];
  included?: JsonApiResource<unknown>[];
  meta?: Record<string, unknown>;
  errors?: JsonApiError[] | Record<string, string[]>;
}

// --- Base API Class ---

export abstract class BaseApi {
  private readonly timeout = 30000;
  protected readonly baseUrl: string;

  constructor(baseUrl: string = '/api/portal') {
    this.baseUrl = baseUrl;
  }

  // --- Public Request Methods (JSON:API) ---

  protected async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const { deserializedData } = await this.request<T>('GET', path, undefined, params);
    return deserializedData;
  }

  protected async getWithMeta<T>(path: string, params?: Record<string, any>): Promise<{ data: T; meta?: Record<string, any> }> {
    const { deserializedData, meta } = await this.request<T>('GET', path, undefined, params);
    return { data: deserializedData, meta };
  }

  protected async postWithMeta<T>(
    path: string,
    body?: unknown,
    params?: Record<string, any>,
    options?: { contentType?: 'json' | 'multipart' }
  ): Promise<{ data: T; meta?: Record<string, any> }> {
    const { deserializedData, meta } = await this.request<T>('POST', path, body, params, options);
    return { data: deserializedData, meta };
  }

  protected async put<T>(
    path: string,
    body?: unknown,
    params?: Record<string, any>,
    options?: { contentType?: 'json' | 'multipart' }
  ): Promise<T> {
    const { deserializedData } = await this.request<T>('PUT', path, body, params, options);
    return deserializedData;
  }

  protected async post<T>(
    path: string,
    body?: unknown,
    params?: Record<string, any>,
    options?: { contentType?: 'json' | 'multipart' }
  ): Promise<T> {
    const { deserializedData } = await this.request<T>('POST', path, body, params, options);
    return deserializedData;
  }

  protected async patch<T>(path: string, body?: unknown, params?: Record<string, any>): Promise<T> {
    const { deserializedData } = await this.request<T>('PATCH', path, body, params);
    return deserializedData;
  }

  protected async delete<T>(path: string, params?: Record<string, string>): Promise<T> {
    const { deserializedData } = await this.request<T>('DELETE', path, undefined, params);
    return deserializedData;
  }

  // --- Public Request Method (Simple JSON) ---

  /**
   * Performs a GET request expecting a simple JSON response (not JSON:API).
   * Optionally transforms response keys to camelCase.
   */
  protected async getSimpleJson<T>(
    path: string,
    params?: Record<string, any>,
    options?: { transformResponseKeys?: boolean }
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Default transformResponseKeys to true if not provided
    const transformKeys = options?.transformResponseKeys ?? true;

    // Transform params to snake_case before creating query string
    const transformedParams = params ? transformKeysToSnake(params) : undefined;
    
    // Filter out undefined, null, and empty string values
    const cleanParams = transformedParams ? Object.entries(transformedParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>) : undefined;
    
    const queryString = cleanParams && Object.keys(cleanParams).length > 0 
      ? `?${new URLSearchParams(cleanParams as any).toString()}` 
      : '';
    const url = `${this.baseUrl}${path}${queryString}`;

    try {
      const headers = this.getHeaders('json'); // Use JSON headers

      const response: Response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'same-origin',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // --- Handle Redirects and No Content ---
      if (response.redirected) {
        // Don't automatically follow redirects - this could cause infinite loops
        // Instead, throw an error that the application can handle
        console.warn('API redirect detected:', response.url);
        throw new StructuredApiError(401, { base: ['Session expired. Please refresh the page.'] }, 'Authentication redirect detected');
      }
      // For simple JSON GET, 204 might mean empty object or null, handle appropriately
      if (response.status === 204) {
        // Decide what to return: empty object or null based on expected T
        return {} as T; // Or potentially null as T;
      }

      // --- Parse JSON Response ---
      let jsonData: any = null;
      try {
        jsonData = await response.json();
      } catch (jsonError) {
        if (!response.ok) {
          const parsedErrors = parseErrorResponse(response.status, null);
          throw new StructuredApiError(response.status, parsedErrors, `HTTP error ${response.status}. Failed to parse response body.`);
        }
        throw new Error("Failed to parse JSON response for a successful request.");
      }

      // --- Handle API Errors ---
      if (!response.ok) {
        // Use the same error parsing logic
        const parsedErrors = parseErrorResponse(response.status, jsonData);
        throw new StructuredApiError(response.status, parsedErrors, undefined, jsonData);
      }

      // --- Transform and Return Simple JSON ---
      if (jsonData === null) {
        return null as T;
      }

      // Handle arrays at the top level
      if (Array.isArray(jsonData)) {
        if (transformKeys) {
          return jsonData.map(item => 
            item && typeof item === 'object' ? transformKeysToCamel(item) : item
          ) as T;
        }
        return jsonData as T;
      }

      // Handle objects
      if (typeof jsonData === 'object') {
        return transformKeys ? transformKeysToCamel(jsonData) as T : jsonData as T;
      }

      throw new Error("Invalid API response: expected an object, array, or null.");

    } catch (error) {
      clearTimeout(timeoutId);
      // Reuse existing error handling logic
      if (error instanceof StructuredApiError) {
        throw error;
      } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new StructuredApiError(408, { base: ['Request timeout'] }, 'Request timeout');
        }
        throw new StructuredApiError(500, { base: [error.message || 'An unexpected network error occurred'] }, error.message);
      } else {
        throw new StructuredApiError(500, { base: ['An unknown error occurred'] }, 'An unknown error occurred');
      }
    }
  }

  /**
   * Performs a POST request expecting a simple JSON response (not JSON:API).
   * Optionally transforms response keys to camelCase.
   */
  protected async postSimpleJson<T>(
    path: string,
    body?: unknown,
    options?: { transformResponseKeys?: boolean }
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Default transformResponseKeys to true if not provided
    const transformKeys = options?.transformResponseKeys ?? true;

    const url = `${this.baseUrl}${path}`;

    try {
      const headers = this.getHeaders('json');
      const transformedBody = body ? transformKeysToSnake(body) : undefined;
      const bodyContent = transformedBody ? JSON.stringify(transformedBody) : undefined;

      const response: Response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: bodyContent,
        credentials: 'same-origin',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // --- Handle Redirects and No Content ---
      if (response.redirected) {
        // Don't automatically follow redirects - this could cause infinite loops
        // Instead, throw an error that the application can handle
        console.warn('API redirect detected:', response.url);
        throw new StructuredApiError(401, { base: ['Session expired. Please refresh the page.'] }, 'Authentication redirect detected');
      }
      // For simple JSON POST, 204 might mean empty object or null, handle appropriately
      if (response.status === 204) {
        // Decide what to return: empty object or null based on expected T
        return {} as T; // Or potentially null as T;
      }

      // --- Parse JSON Response ---
      let jsonData: any = null;
      try {
        jsonData = await response.json();
      } catch (jsonError) {
        if (!response.ok) {
          const parsedErrors = parseErrorResponse(response.status, null);
          throw new StructuredApiError(response.status, parsedErrors, `HTTP error ${response.status}. Failed to parse response body.`);
        }
        throw new Error("Failed to parse JSON response for a successful request.");
      }

      // --- Handle API Errors ---
      if (!response.ok) {
        // Use the same error parsing logic
        const parsedErrors = parseErrorResponse(response.status, jsonData);
        throw new StructuredApiError(response.status, parsedErrors, undefined, jsonData);
      }

      // --- Transform and Return Simple JSON ---
      if (jsonData === null) {
        return null as T;
      }

      // Handle arrays at the top level
      if (Array.isArray(jsonData)) {
        if (transformKeys) {
          return jsonData.map(item => 
            item && typeof item === 'object' ? transformKeysToCamel(item) : item
          ) as T;
        }
        return jsonData as T;
      }

      // Handle objects
      if (typeof jsonData === 'object') {
        return transformKeys ? transformKeysToCamel(jsonData) as T : jsonData as T;
      }

      throw new Error("Invalid API response: expected an object, array, or null.");

    } catch (error) {
      clearTimeout(timeoutId);
      // Reuse existing error handling logic
      if (error instanceof StructuredApiError) {
        throw error;
      } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new StructuredApiError(408, { base: ['Request timeout'] }, 'Request timeout');
        }
        throw new StructuredApiError(500, { base: [error.message || 'An unexpected network error occurred'] }, error.message);
      } else {
        throw new StructuredApiError(500, { base: ['An unknown error occurred'] }, 'An unknown error occurred');
      }
    }
  }

  /**
   * Performs a PUT request expecting a simple JSON response (not JSON:API).
   * Optionally transforms response keys to camelCase.
   */
  protected async putSimpleJson<T>(
    path: string,
    body?: unknown,
    options?: { transformResponseKeys?: boolean }
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Default transformResponseKeys to true if not provided
    const transformKeys = options?.transformResponseKeys ?? true;

    const url = `${this.baseUrl}${path}`;

    try {
      const headers = this.getHeaders('json');
      const transformedBody = body ? transformKeysToSnake(body) : undefined;
      const bodyContent = transformedBody ? JSON.stringify(transformedBody) : undefined;

      const response: Response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: bodyContent,
        credentials: 'same-origin',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // --- Handle Redirects and No Content ---
      if (response.redirected) {
        // Don't automatically follow redirects - this could cause infinite loops
        // Instead, throw an error that the application can handle
        console.warn('API redirect detected:', response.url);
        throw new StructuredApiError(401, { base: ['Session expired. Please refresh the page.'] }, 'Authentication redirect detected');
      }
      // For simple JSON PUT, 204 might mean empty object or null, handle appropriately
      if (response.status === 204) {
        // Decide what to return: empty object or null based on expected T
        return {} as T; // Or potentially null as T;
      }

      // --- Parse JSON Response ---
      let jsonData: any = null;
      try {
        jsonData = await response.json();
      } catch (jsonError) {
        if (!response.ok) {
          const parsedErrors = parseErrorResponse(response.status, null);
          throw new StructuredApiError(response.status, parsedErrors, `HTTP error ${response.status}. Failed to parse response body.`);
        }
        throw new Error("Failed to parse JSON response for a successful request.");
      }

      // --- Handle API Errors ---
      if (!response.ok) {
        // Use the same error parsing logic
        const parsedErrors = parseErrorResponse(response.status, jsonData);
        throw new StructuredApiError(response.status, parsedErrors, undefined, jsonData);
      }

      // --- Transform and Return Simple JSON ---
      if (jsonData === null) {
        return null as T;
      }

      // Handle arrays at the top level
      if (Array.isArray(jsonData)) {
        if (transformKeys) {
          return jsonData.map(item => 
            item && typeof item === 'object' ? transformKeysToCamel(item) : item
          ) as T;
        }
        return jsonData as T;
      }

      // Handle objects
      if (typeof jsonData === 'object') {
        return transformKeys ? transformKeysToCamel(jsonData) as T : jsonData as T;
      }

      throw new Error("Invalid API response: expected an object, array, or null.");

    } catch (error) {
      clearTimeout(timeoutId);
      // Reuse existing error handling logic
      if (error instanceof StructuredApiError) {
        throw error;
      } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new StructuredApiError(408, { base: ['Request timeout'] }, 'Request timeout');
        }
        throw new StructuredApiError(500, { base: [error.message || 'An unexpected network error occurred'] }, error.message);
      } else {
        throw new StructuredApiError(500, { base: ['An unknown error occurred'] }, 'An unknown error occurred');
      }
    }
  }

  // --- Core Request Logic (JSON:API) ---

  protected async request<T>(
    method: HttpMethod | 'PATCH',
    path: string,
    body?: unknown,
    params?: Record<string, any>,
    options?: { contentType?: 'json' | 'multipart' }
  ): Promise<{ deserializedData: T; meta?: Record<string, unknown> }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Transform params to snake_case before creating query string
    const transformedParams = params ? transformKeysToSnake(params) : undefined;
    
    // Filter out undefined, null, and empty string values
    const cleanParams = transformedParams ? Object.entries(transformedParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>) : undefined;
    
    const queryString = cleanParams && Object.keys(cleanParams).length > 0 
      ? `?${new URLSearchParams(cleanParams as any).toString()}` 
      : '';
    const url = `${this.baseUrl}${path}${queryString}`;

    try {
      const contentType = options?.contentType || (body instanceof FormData ? 'multipart' : 'json');
      const transformedBody = (method !== 'GET' && body && contentType === 'json')
        ? transformKeysToSnake(body)
        : body;
      const bodyContent = (transformedBody && contentType === 'json')
        ? JSON.stringify(transformedBody)
        : transformedBody;
      const headers = this.getHeaders(contentType === 'json' ? 'json' : 'form');

      const response: Response = await fetch(url, {
        method,
        headers: headers,
        body: (method !== 'GET' && bodyContent) ? bodyContent as any : undefined,
        credentials: 'same-origin',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // --- Handle Redirects and No Content ---
      if (response.redirected) {
        // Don't automatically follow redirects - this could cause infinite loops
        // Instead, throw an error that the application can handle
        console.warn('API redirect detected:', response.url);
        throw new StructuredApiError(401, { base: ['Session expired. Please refresh the page.'] }, 'Authentication redirect detected');
      }
      if (response.status === 204) {
        // For JSON:API, 204 usually means success with no body to deserialize
        return { deserializedData: {} as T, meta: undefined };
      }

      // --- Parse JSON Response ---
      let jsonData: JsonApiDocument | any = null;
      try {
        jsonData = await response.json();
      } catch (jsonError) {
        if (!response.ok) {
          const parsedErrors = parseErrorResponse(response.status, null);
          throw new StructuredApiError(response.status, parsedErrors, `HTTP error ${response.status}. Failed to parse response body.`);
        }
        throw new Error("Failed to parse JSON response for a successful request.");
      }

      // --- Handle API Errors ---
      if (!response.ok) {
        console.error(`API Error - Status: ${response.status}, URL: ${url}`, jsonData);
        const parsedErrors = parseErrorResponse(response.status, jsonData);
        throw new StructuredApiError(response.status, parsedErrors, undefined, jsonData);
      }

      // --- Deserialize JSON:API Response ---
      if (!jsonData || typeof jsonData !== 'object' || !('data' in jsonData)) {
         // Basic check for JSON:API structure (presence of 'data' key)
         console.error("Invalid JSON:API response structure received:", jsonData);
         throw new Error("Invalid API response: Expected JSON:API structure.");
      }

      const deserializedData = this.deserializeJsonApiDocument<T>(jsonData);
      const meta = jsonData.meta ? transformKeysToCamel(jsonData.meta) : undefined;

      return { deserializedData, meta };

    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof StructuredApiError) {
        throw error;
      } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new StructuredApiError(408, { base: ['Request timeout'] }, 'Request timeout');
        }
        throw new StructuredApiError(500, { base: [error.message || 'An unexpected network error occurred'] }, error.message);
      } else {
        throw new StructuredApiError(500, { base: ['An unknown error occurred'] }, 'An unknown error occurred');
      }
    }
  }

  // --- JSON:API Deserialization Helper ---
  private deserializeJsonApiDocument<T>(doc: JsonApiDocument): T {
    if (!doc.data) {
      return null as T;
    }

    const includedMap = new Map<string, JsonApiResourceObject>();
    if (doc.included) {
      for (const resource of doc.included) {
        includedMap.set(`${resource.type}:${resource.id}`, resource);
      }
    }

    const deserializeResource = (resource: JsonApiResourceObject): Record<string, any> => {
      const deserialized: Record<string, any> = {
        id: resource.id,
        ...(resource.attributes ? transformKeysToCamel(resource.attributes) : {})
      };

      if (resource.relationships) {
        for (const relName in resource.relationships) {
          const relationship = resource.relationships[relName];
          const camelRelName = transformKeysToCamel({ [relName]: null });
          const finalRelName = Object.keys(camelRelName)[0];

          if (relationship.data) {
            if (Array.isArray(relationship.data)) {
              deserialized[finalRelName] = relationship.data
                .map(identifier => {
                  const lookupKey = identifier.type === 'index' ? identifier.id : `${identifier.type}:${identifier.id}`;
                  const includedResource = includedMap.get(lookupKey);
                  return includedResource ? deserializeResource(includedResource) : { id: identifier.id, type: identifier.type };
                })
                .filter(Boolean);
            } else {
              const identifier = relationship.data;
              const lookupKey = identifier.type === 'index' ? identifier.id : `${identifier.type}:${identifier.id}`;
              const includedResource = includedMap.get(lookupKey);
              deserialized[finalRelName] = includedResource ? deserializeResource(includedResource) : { id: identifier.id, type: identifier.type };
            }
          } else {
            deserialized[finalRelName] = null;
          }
        }
      }
      return deserialized;
    };

    if (Array.isArray(doc.data)) {
      return doc.data.map(deserializeResource) as T;
    } else {
      return deserializeResource(doc.data) as T;
    }
  }

  // --- Backward Compatibility Helper ---
  protected mapJsonApiResponse<T>(data: JsonApiResource<Omit<T, 'id'>>): T {
    const mapped = {
      id: data.id,
      ...data.attributes
    };
    return transformKeysToCamel(mapped) as T;
  }

  // --- Header Generation ---
  protected getHeaders(type: 'json' | 'form' = 'json'): Record<string, string> {
    // Retrieve CSRF token
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

    // Retrieve JWT from localStorage
    const jwtToken = localStorage.getItem('jwtToken');

    const baseHeaders: Record<string, string> = {
      'X-CSRF-Token': csrfToken
    };

    if (jwtToken) {
      baseHeaders['Authorization'] = `Bearer ${jwtToken}`;
    }

    if (type === 'json') {
      return {
        ...baseHeaders,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
    } else { // FormData
      return {
        ...baseHeaders,
        'Accept': 'application/json',
      };
    }
  }
}
